import {
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  Box,
  Snackbar,
  SnackbarContent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState, useCallback } from "react";
import {
  createNewUser,
  deleteUser,
  getUsers,
  updateUser,
} from "src/services/users";
import * as Yup from "yup";
import {
  ButtonContainer,
  InputContainer,
  ModalContainer,
  UserContainter,
} from "./styles";
import UserList from "../UserList";
import { formatCPF, validateCPF } from "src/utils/formatters";

interface IUserModal {
  name: string;
  cpf: string;
  id: number;
  role: "admin" | "normal";
}

interface INewUserModal {
  open: boolean;
  handleClose: () => void;
}

const getValidationSchema = (isEditing: boolean) =>
  Yup.object({
    name: Yup.string()
      .required("Nome é obrigatório")
      .test("nome-sobrenome", "Informe nome e sobrenome", (value) => {
        if (!value) return false;
        return value.trim().split(" ").length >= 2;
      }),
    cpf: Yup.string()
      .required("CPF é obrigatório")
      .test(
        "cpf-valido",
        "CPF inválido",
        (value) => !!value && validateCPF(value),
      ),
    password: isEditing
      ? Yup.string().notRequired()
      : Yup.string()
        .required("Senha é obrigatória")
        .min(8, "A senha deve ter pelo menos 8 caracteres"),
    passwordConfirmation: isEditing
      ? Yup.string().notRequired()
      : Yup.string()
        .required("Confirmação de senha é obrigatória")
        .oneOf([Yup.ref("password")], "As senhas devem coincidir"),
  });

export default function NewUserModal({ open, handleClose }: INewUserModal) {
  const [users, setUsers] = useState<IUserModal[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [creatingNewUser, setCreatingNewUser] = useState<boolean>(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [toast, setToast] = useState<any>(null);
  const [editingUser, setEditingUser] = useState<IUserModal | null>(null);

  const getLoggedUserRole = useCallback(() => {
    const stored = localStorage.getItem("meData");
    if (!stored) return null;
    try {
      const { role } = JSON.parse(stored);
      return role;
    } catch {
      return null;
    }
  }, []);

  const listUsers = async () => {
    if (getLoggedUserRole() !== "admin") return;

    try {
      setLoadingUsers(true);
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      setToast({ message: "Erro ao carregar usuários", type: "error" });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEdit = (user: IUserModal) => {
    setEditingUser(user);
  };

  const handleSubmit = async (values: any, resetForm: () => void) => {
    if (getLoggedUserRole() !== "admin") {
      setToast({
        message: "Acesso negado: você não é um administrador.",
        type: "error",
      });
      return;
    }

    try {
      setCreatingNewUser(true);

      const payload: any = {
        name: values.name,
        cpf: values.cpf.replace(/\D/g, ""),
        role: values.role,
      };

      if (values.password) {
        payload.password = values.password;
        payload.password_confirmation = values.passwordConfirmation;
      }

      let response;
      if (editingUser) {
        response = await updateUser(editingUser.id, payload);
        if (response) {
          setToast({
            message: "Usuário atualizado com sucesso! ✅",
            type: "success",
          });
          resetForm();
          setEditingUser(null);
          listUsers();
        }
      } else {
        response = await createNewUser(payload);
        if (response?.status === 200 || response?.status === 201) {
          setToast({
            message: "Usuário criado com sucesso! ✅",
            type: "success",
          });
          resetForm();
          setEditingUser(null);
          listUsers();
        } else {
          setToast({
            message: "Ocorreu um erro ao criar usuário",
            type: "error",
          });
        }
      }
    } catch (error) {
      setToast({ message: "Ocorreu um erro ao salvar usuário", type: "error" });
    } finally {
      setCreatingNewUser(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (getLoggedUserRole() !== "admin") {
      setToast({ message: "Permissão negada para excluir.", type: "error" });
      return;
    }

    const confirmDelete = window.confirm(
      "Tem certeza que deseja excluir este usuário?",
    );
    if (!confirmDelete) return;

    try {
      setDeletingUserId(id);
      await deleteUser(id);
      setToast({
        message: "Usuário excluído com sucesso! ✅",
        type: "success",
      });
    } catch (error) {
      setToast({ message: "Erro ao deletar usuário ❌", type: "error" });
    } finally {
      setDeletingUserId(null);
      listUsers();
    }
  };

  useEffect(() => {
    if (open) {
      const role = getLoggedUserRole();
      if (role !== "admin") {
        alert(
          "Acesso restrito. Apenas administradores podem gerenciar usuários.",
        );
        handleClose();
      } else {
        listUsers();
      }
    }
  }, [open, handleClose, getLoggedUserRole]);

  if (open && getLoggedUserRole() !== "admin") return null;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <UserContainter>
            <Typography
              id="modal-title"
              component="h2"
              variant="h6"
              gutterBottom
            >
              {editingUser ? "Editar Usuário" : "Novo Usuário"}
            </Typography>

            <Formik
              enableReinitialize
              initialValues={{
                name: editingUser?.name || "",
                cpf: editingUser?.cpf ? formatCPF(editingUser.cpf) : "",
                password: "",
                passwordConfirmation: "",
                role: editingUser?.role || "normal",
              }}
              validationSchema={getValidationSchema(!!editingUser)}
              onSubmit={(values, { resetForm }) =>
                handleSubmit(values, resetForm)
              }
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
              }) => (
                <Form>
                  <InputContainer>
                    <TextField
                      fullWidth
                      required
                      label="Nome completo"
                      size="small"
                      margin="normal"
                      name="name"
                      disabled={!!editingUser}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                    <TextField
                      fullWidth
                      required
                      label="CPF"
                      size="small"
                      margin="normal"
                      name="cpf"
                      disabled={!!editingUser}
                      value={values.cpf}
                      onChange={(e) =>
                        setFieldValue("cpf", formatCPF(e.target.value))
                      }
                      onBlur={handleBlur}
                      error={touched.cpf && Boolean(errors.cpf)}
                      helperText={touched.cpf && errors.cpf}
                      inputProps={{ maxLength: 14 }}
                    />
                  </InputContainer>

                  <InputContainer>
                    <TextField
                      fullWidth
                      label={editingUser ? "Nova Senha (opcional)" : "Senha"}
                      type="password"
                      size="small"
                      margin="normal"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                    <TextField
                      fullWidth
                      label="Confirmar Senha"
                      type="password"
                      size="small"
                      margin="normal"
                      name="passwordConfirmation"
                      value={values.passwordConfirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.passwordConfirmation &&
                        Boolean(errors.passwordConfirmation)
                      }
                      helperText={
                        touched.passwordConfirmation &&
                        errors.passwordConfirmation
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.role === "admin"}
                          onChange={(e) =>
                            setFieldValue(
                              "role",
                              e.target.checked ? "admin" : "normal",
                            )
                          }
                          color="primary"
                        />
                      }
                      label="Administrador"
                    />
                  </InputContainer>

                  <ButtonContainer>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingUser(null);
                        handleClose();
                      }}
                      disabled={creatingNewUser}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={creatingNewUser}
                    >
                      {creatingNewUser ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Salvar"
                      )}
                    </Button>
                  </ButtonContainer>

                  {loadingUsers ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress color="primary" />
                    </Box>
                  ) : (
                    <UserList
                      users={users}
                      onDelete={handleDelete}
                      deletingUserId={deletingUserId}
                      onEdit={handleEdit}
                    />
                  )}
                </Form>
              )}
            </Formik>
          </UserContainter>
        </ModalContainer>
      </Modal>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>
    </>
  );
}
